import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import { useTestDescriptions } from "@app/components/common/testDescription";
import useTestTitles from "@app/hooks/useTestTitles";
import { TestType } from "@app/provider/testData/types";
import { routes } from "@app/routing";
import {
  clearGisReturnUrl,
  GIS_ENTRY_SOURCE_PUBLIC,
} from "@app/utils/gisEntrySource";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";

import {
  GIS_SELECTED_ORDER_PARAM,
  useEligibleGisOrders,
} from "./useEligibleGisOrders";

import "./publicGisTriage.scss";

/**
 * Multi-order picker for the public GIS scheduling funnel.
 *
 * `PublicGisTriage` auto-routes when a patient has exactly one GIS-eligible
 * order. When more than one exists it can't choose on the patient's behalf, so
 * it redirects here. The patient picks an order and we route back to triage
 * with the chosen order's uid (`?orderUid=<uid>`), which then resolves the
 * correct downstream experience (ONC intake vs WH Acuity iframe) for that
 * specific order — keeping all routing rules in one place (triage).
 *
 * Loading / error / no-orders / single-order edge cases mirror the triage
 * page so a patient who lands here directly (deep link, refresh) still gets a
 * coherent experience:
 *   - 0 eligible orders   → triage handles the "no orders" empty state.
 *   - exactly 1 eligible  → bounce straight to triage (it'll auto-route).
 *   - load error          → "load error" with Retry.
 */
const messages = defineMessages({
  pageTitle: {
    id: "publicGisOrderSelect.pageTitle",
    defaultMessage:
      "Please select which test you're requesting a genetic information session for.",
  },
  orderedOn: {
    id: "publicGisOrderSelect.orderedOn",
    defaultMessage: "Ordered {date}",
  },
  next: {
    id: "publicGisOrderSelect.next",
    defaultMessage: "Next",
  },
  back: {
    id: "publicGisOrderSelect.back",
    defaultMessage: "Back",
  },
  loadErrorTitle: {
    id: "publicGisOrderSelect.loadErrorTitle",
    defaultMessage: "We couldn't load your orders",
  },
  loadErrorBody: {
    id: "publicGisOrderSelect.loadErrorBody",
    defaultMessage:
      "Something went wrong while loading your orders. Please try again.",
  },
  retry: {
    id: "publicGisOrderSelect.retry",
    defaultMessage: "Try again",
  },
  backToHome: {
    id: "publicGisOrderSelect.backToHome",
    defaultMessage: "Back to home",
  },
});

/**
 * Fallback product label used only when a `TestType` has no localized title in
 * `useTestTitles` (e.g. a newly added enum value). Most GIS-eligible product
 * types are brand names, so a title-cased enum reads correctly (SIGNATERA →
 * Signatera). A small override map covers acronyms that shouldn't be
 * title-cased.
 *
 * Note: do NOT rely on this for known test types. Some labels diverge from the
 * enum name (SINGLE_GENE_NIPT → "Fetal Focus"), so the localized title from
 * `useTestTitles` is the source of truth.
 */
const TEST_TYPE_LABEL_OVERRIDES: Partial<Record<TestType, string>> = {
  [TestType.DPYD]: "DPYD",
};

function formatTestTypeLabel(testType: TestType): string {
  return (
    TEST_TYPE_LABEL_OVERRIDES[testType] ??
    testType.charAt(0).toUpperCase() + testType.slice(1).toLowerCase()
  );
}
const PublicGisOrderSelect: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { eligibleOrders, isLoading, loadError, retry } =
    useEligibleGisOrders();
  const testDescriptions = useTestDescriptions();
  const testTitles = useTestTitles();

  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const selectedOrder = useMemo(
    () => eligibleOrders.find((order) => order.uid === selectedUid) ?? null,
    [eligibleOrders, selectedUid]
  );

  useEffect(() => {
    heap.track(HEAP_EVENTS.upp_view_gis_order_select, {
      location: HeapEventLocation.gis_order_select_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
    });
  }, []);

  // If a patient lands here without a real multi-order situation (deep link,
  // refresh after one order resolved, etc.), hand control back to triage so
  // the single source of routing truth decides what to show: triage renders
  // the "no orders" state for 0 eligible orders and auto-routes for exactly 1.
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (isLoading || loadError) return;
    if (eligibleOrders.length <= 1 && !redirectedRef.current) {
      redirectedRef.current = true;
      navigate(routes.gisTriage, { replace: true });
    }
  }, [isLoading, loadError, eligibleOrders, navigate]);

  const handleNext = useCallback(() => {
    if (!selectedOrder) return;
    heap.track(HEAP_EVENTS.upp_gis_order_select, {
      location: HeapEventLocation.gis_order_select_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
      test_type: selectedOrder.testType,
      status: selectedOrder.status,
    });
    navigate(
      `${routes.gisTriage}?${GIS_SELECTED_ORDER_PARAM}=${encodeURIComponent(
        selectedOrder.uid
      )}`
    );
  }, [navigate, selectedOrder]);

  const handleBackToHome = useCallback(() => {
    clearGisReturnUrl();
    navigate(routes.home);
  }, [navigate]);

  if (loadError) {
    return (
      <div className="public-gis-triage">
        <div
          className="public-gis-triage__empty"
          role="alert"
          aria-live="polite"
        >
          <h1 className="public-gis-triage__empty-title">
            {intl.formatMessage(messages.loadErrorTitle)}
          </h1>
          <p className="public-gis-triage__empty-body">
            {intl.formatMessage(messages.loadErrorBody)}
          </p>
          <div className="public-gis-triage__actions">
            <button
              type="button"
              className="public-gis-triage__primary-btn"
              onClick={retry}
            >
              {intl.formatMessage(messages.retry)}
            </button>
            <button
              type="button"
              className="public-gis-triage__back-btn"
              onClick={handleBackToHome}
            >
              {intl.formatMessage(messages.backToHome)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Still fetching, or a non-multi-order situation we're about to bounce back
  // to triage for (handled by the effect above) — keep the loader up.
  if (isLoading || eligibleOrders.length <= 1) {
    return (
      <div className="public-gis-triage">
        <div className="public-gis-triage__loader">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="public-gis-triage">
      <div className="public-gis-triage__select">
        <h1 className="public-gis-triage__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        <fieldset className="public-gis-triage__order-group">
          <legend className="public-gis-triage__visually-hidden">
            {intl.formatMessage(messages.pageTitle)}
          </legend>
          {eligibleOrders.map((order) => {
            const isSelected = order.uid === selectedUid;
            const description =
              testDescriptions[order.testType] ||
              intl.formatMessage(messages.orderedOn, {
                date: intl.formatDate(new Date(order.createdAt), {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
              });
            return (
              <label
                key={order.uid}
                className="public-gis-triage__order-option"
              >
                <input
                  type="radio"
                  name="gis-order"
                  className="public-gis-triage__order-radio"
                  value={order.uid}
                  checked={isSelected}
                  onChange={() => setSelectedUid(order.uid)}
                />
                <span className="public-gis-triage__order-text">
                  <span className="public-gis-triage__order-name">
                    {testTitles[order.testType] ||
                      formatTestTypeLabel(order.testType)}
                  </span>
                  {description && (
                    <span className="public-gis-triage__order-desc">
                      {description}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </fieldset>
        <div className="public-gis-triage__actions-stacked">
          <button
            type="button"
            className="public-gis-triage__primary-btn"
            disabled={!selectedOrder}
            onClick={handleNext}
          >
            {intl.formatMessage(messages.next)}
          </button>
          <button
            type="button"
            className="public-gis-triage__back-btn"
            onClick={handleBackToHome}
          >
            {intl.formatMessage(messages.back)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicGisOrderSelect;
